import { useEffect, type FunctionComponent } from 'react';
import { FormattedMessage } from 'react-intl';

import PeopleGroupIcon from '@atlaskit/icon/core/people-group';
import SettingsGearIcon from '@atlaskit/icon/core/settings';
import { Analytics, formatContainers } from '@trello/atlassian-analytics';
import { boardSwitcherSharedState } from '@trello/board-switcher';
import { Button } from '@trello/nachos/button';
import { Icon } from '@trello/nachos/icon';
import {
  getOrganizationAccountUrl,
  getOrganizationMembersUrl,
} from '@trello/urls';

import { OfferingLozenge } from 'app/src/components/OfferingLozenge';
import { useBoardSwitcherWorkspaceFragment } from './BoardSwitcherWorkspaceFragment.generated';
import { isRealWorkspace } from './isRealWorkspace';
import { JiraProjects } from './JiraProjects';
import { useBoardSwitcher } from './useBoardSwitcher';
import { useIsGuestWorkspace } from './useIsGuestWorkspace';
import { WorkspaceBoards } from './WorkspaceBoards';
import { useWorkspaceBoardsSectionPrefetchLazyQuery } from './WorkspaceBoardsSectionPrefetchQuery.generated';
import { WorkspaceMoreButton } from './WorkspaceMoreButton';
import { WorkspaceViews } from './WorkspaceViews';

import * as styles from './SelectedWorkspace.module.less';

const MembersIcon = () => (
  <Icon
    glyph={() => <PeopleGroupIcon label="" />}
    dangerous_className={styles.icon}
  />
);
const SettingsIcon = () => (
  <Icon
    glyph={() => <SettingsGearIcon label="" />}
    dangerous_className={styles.icon}
  />
);

interface SelectedWorkspaceProps {
  workspaceId: string;
}

export const SelectedWorkspace: FunctionComponent<SelectedWorkspaceProps> = ({
  workspaceId,
}) => {
  const { workspaceBoards } = useBoardSwitcher();

  const fragmentId = isRealWorkspace(workspaceId) ? workspaceId : null;
  const [prefetchWorkspaceData] = useWorkspaceBoardsSectionPrefetchLazyQuery();
  const { data } = useBoardSwitcherWorkspaceFragment({
    from: { id: fragmentId },
    returnPartialData: true,
  });

  const isGuestWorkspace = useIsGuestWorkspace(workspaceId);

  const boardIds = workspaceBoards.get(workspaceId) ?? [];

  useEffect(() => {
    if (isGuestWorkspace) {
      return;
    }

    prefetchWorkspaceData({
      variables: { id: workspaceId },
    });
  }, [workspaceId, isGuestWorkspace, prefetchWorkspaceData]);

  if (isGuestWorkspace && boardIds.length > 0) {
    return (
      <div
        className={styles.selectedWorkspace}
        role="tabpanel"
        id={`workspace-tabpanel-${workspaceId}`}
        aria-labelledby={`workspace-tab-${workspaceId}`}
      >
        <WorkspaceBoards workspaceId={workspaceId} boardIds={boardIds} />
        <footer>
          <OfferingLozenge
            offering="guest"
            className={styles.offeringLozenge}
          />
        </footer>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const handleMembersClick = () => {
    Analytics.sendClickedLinkEvent({
      linkName: 'selectedWorkspaceMembersLink',
      source: 'boardSwitcherModal',
      containers: formatContainers({
        workspaceId,
      }),
    });

    boardSwitcherSharedState.setValue({
      isOpen: false,
    });
  };

  const handleSettingsClick = () => {
    Analytics.sendClickedLinkEvent({
      linkName: 'selectedWorkspaceSettingsLink',
      source: 'boardSwitcherModal',
      containers: formatContainers({
        workspaceId,
      }),
    });

    boardSwitcherSharedState.setValue({
      isOpen: false,
    });
  };

  return (
    <div
      className={styles.selectedWorkspace}
      role="tabpanel"
      id={`workspace-tabpanel-${workspaceId}`}
      aria-labelledby={`workspace-tab-${workspaceId}`}
    >
      <section>
        <WorkspaceBoards
          workspaceId={workspaceId}
          boardIds={boardIds}
          showCreateBoardTile
        />
        <JiraProjects workspaceId={workspaceId} />
        <WorkspaceViews
          workspaceId={workspaceId}
          enterpriseId={data?.idEnterprise ?? undefined}
        />
      </section>
      <footer>
        <OfferingLozenge
          offering={data.offering ?? ''}
          className={styles.offeringLozenge}
        />
        <div className={styles.buttons}>
          <Button
            iconBefore={<MembersIcon />}
            appearance="subtle"
            isRouterLink
            href={getOrganizationMembersUrl(data)}
            onClick={handleMembersClick}
          >
            <FormattedMessage
              id="templates.board_switcher.members"
              defaultMessage="Members"
              description="Text on button that opens a workspace's members page"
            />
          </Button>
          <Button
            iconBefore={<SettingsIcon />}
            appearance="subtle"
            isRouterLink
            href={getOrganizationAccountUrl(data)}
            onClick={handleSettingsClick}
          >
            <FormattedMessage
              id="templates.board_switcher.settings"
              defaultMessage="Settings"
              description="Text on button that opens a workspace's settings page"
            />
          </Button>
          <WorkspaceMoreButton workspaceId={workspaceId} />
        </div>
      </footer>
    </div>
  );
};
